import { useCallback, useEffect, useMemo, useState } from "react";
import {
  apiCall,
  readStoredToken,
  storeToken,
  UNAUTHORIZED_EVENT,
} from "@/lib/shopApi";

/**
 * useShopApi — the single place where the CRM shop API is called
 * (pattern from briefing §7.1, extended with boot state + 401 handling).
 */

// Korte cache op de productlijst: meerdere onderdelen (catalogus, categorieënmenu,
// mandje, snelbestellen) vragen dezelfde lijst op; zonder dedup levert dat
// onnodige, langzame CRM-calls op.
const PRODUCTEN_TTL = 30_000;
const productenCache = new Map();

function fetchProducten(call, q) {
  const key = q || "";
  const nu = Date.now();
  const cached = productenCache.get(key);
  if (cached && (cached.pending || nu - cached.tijd < PRODUCTEN_TTL)) return cached.promise;

  const promise = call("GET", `/shop/products${q ? `?q=${encodeURIComponent(q)}` : ""}`)
    .then((data) => {
      productenCache.set(key, { promise: Promise.resolve(data), tijd: Date.now(), pending: false });
      return data;
    })
    .catch((err) => {
      productenCache.delete(key);
      throw err;
    });
  productenCache.set(key, { promise, tijd: nu, pending: true });
  return promise;
}

export function clearProductenCache() {
  productenCache.clear();
}
export function useShopApi() {
  const [token, setToken] = useState(() => readStoredToken());
  const [account, setAccount] = useState(null);
  const [booting, setBooting] = useState(() => Boolean(readStoredToken()));
  const [verzendregels, setVerzendregels] = useState(null);

  const call = useCallback(
    (method, path, body) => apiCall(method, path, { body, token }),
    [token],
  );

  const logout = useCallback(() => {
    clearProductenCache();
    storeToken(null);
    setToken(null);
    setAccount(null);
  }, []);

  // Recover the session on refresh via /shop/auth/me
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setAccount(null);
      setBooting(false);
      return () => {
        cancelled = true;
      };
    }
    if (account) {
      setBooting(false);
      return () => {
        cancelled = true;
      };
    }
    setBooting(true);
    apiCall("GET", "/shop/auth/me", { token })
      .then((data) => {
        if (cancelled) return;
        setAccount(data?.account ? data.account : data);
      })
      .catch(() => {
        if (cancelled) return;
        storeToken(null);
        setToken(null);
        setAccount(null);
      })
      .finally(() => {
        if (!cancelled) setBooting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, account]);

  /**
   * Verzendregels uit het CRM (o.a. minimum bestelbedrag per land). Bestaat het
   * endpoint nog niet, dan blijft dit null en geldt de terugval uit het beheer.
   */
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setVerzendregels(null);
      return () => { cancelled = true; };
    }
    apiCall("GET", "/shop/verzendregels", { token })
      .then((data) => { if (!cancelled) setVerzendregels(data?.verzendregels || data || null); })
      .catch(() => { if (!cancelled) setVerzendregels(null); });
    return () => { cancelled = true; };
  }, [token]);

  // Any 401 anywhere in the app → logout
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener(UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handler);
  }, [logout]);

  const login = useCallback(async (email, wachtwoord) => {
    const data = await apiCall("POST", "/shop/auth/login", {
      body: { email, wachtwoord },
    });
    storeToken(data.token);
    setToken(data.token);
    // /shop/auth/me bevat ook de organisatie (o.a. land) — nodig voor de margeberekening.
    let account = data.account;
    try {
      const me = await apiCall("GET", "/shop/auth/me", { token: data.token });
      account = me?.account || me || account;
    } catch (e) {
      /* login mag niet falen op deze extra call */
    }
    setAccount(account);
    return account;
  }, []);

  return useMemo(
    () => ({
      token,
      account,
      verzendregels,
      booting,
      isAuthenticated: Boolean(token),
      login,
      logout,
      register: (data) => apiCall("POST", "/shop/auth/register", { body: data }),
      producten: (q) => fetchProducten(call, q),
      product: (id) => call("GET", `/shop/products/${id}`),
      bestellen: (regels, opmerking, betaling, extra = {}) =>
        call("POST", "/shop/orders", {
          regels,
          opmerking,
          ...(betaling ? { betaling } : {}),
          ...extra,
        }),
      bestellingen: () => call("GET", "/shop/orders"),
      /**
       * Adreswijziging aanvragen. Het CRM zet deze in afwachting van goedkeuring
       * (afgesproken contract: POST /shop/account/adres-wijziging).
       */
      adresWijziging: (bezorgadres, factuuradres) =>
        call("POST", "/shop/account/adres-wijziging", { bezorgadres, factuuradres }),
      /** Wachtwoord wijzigen door de ingelogde klant (CRM bewaart het wachtwoord). */
      wachtwoordWijzigen: (huidig_wachtwoord, nieuw_wachtwoord) =>
        call("POST", "/shop/account/wachtwoord", { huidig_wachtwoord, nieuw_wachtwoord }),
      /** Accountgegevens opnieuw ophalen na een wijziging. */
      refreshAccount: async () => {
        const me = await apiCall("GET", "/shop/auth/me", { token });
        const vers = me?.account || me;
        setAccount(vers);
        return vers;
      },
    }),
    [token, account, verzendregels, booting, login, logout, call],
  );
}
